import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IAdminProduct,
  type IAdminWarehouseProductList,
  type IFeatureFlag,
  type IReportProductScoring,
  type IReportWarehouseProductToImport,
  type IWarehouseProductFeatureFlag,
} from '@mahawi/eshop-common/src/types';
import { type ColumnFilterItem } from 'antd/es/table/interface';
import { Key } from 'react';

import { type IFeatureFlagsState } from '../reducers/feature-flags/types';

type Product =
  | IAdminWarehouseProductList
  | IReportWarehouseProductToImport
  | IAdminProduct
  | IReportProductScoring;

const FILTER_WITH_FEATURE_FLAGS: string = 'withFeatureFlags';
const FILTER_WITHOUT_FEATURE_FLAGS: string = 'withoutFeatureFlags';

export function getTableFeatureFlagsColumnFilters({
  dataSource,
  FeatureFlags,
}: {
  dataSource:
    | IAdminWarehouseProductList[]
    | IReportWarehouseProductToImport[]
    | IAdminProduct[]
    | IReportProductScoring[];
  FeatureFlags: IFeatureFlagsState;
}): ColumnFilterItem[] {
  const columnFilterItemFeatureFlags: Map<string, ColumnFilterItem> = new Map<
    string,
    ColumnFilterItem
  >();

  dataSource.forEach((product: Product): void => {
    product.featureFlags?.forEach((ff: IWarehouseProductFeatureFlag): void => {
      if (columnFilterItemFeatureFlags.has(ff.code)) {
        return;
      }

      const eff: string | undefined = FeatureFlags.featureFlags?.find(
        (f: IFeatureFlag): boolean => f.code === ff.code,
      )?.name;

      columnFilterItemFeatureFlags.set(ff.code, {
        text: eff ? eff : ff.code,
        value: ff.code,
      });
    });
  });

  const sortedEntries: ColumnFilterItem[] = Array.from(
    columnFilterItemFeatureFlags.values(),
  ).sort((a: ColumnFilterItem, b: ColumnFilterItem): number =>
    stringCompare(String(a.value), String(b.value)),
  );

  const defaultFilterItems: ColumnFilterItem[] = [
    {
      text: 'With feature flags',
      value: FILTER_WITH_FEATURE_FLAGS,
    },
    {
      text: 'Without feature flags',
      value: FILTER_WITHOUT_FEATURE_FLAGS,
    },
  ];

  return [...defaultFilterItems, ...sortedEntries];
}

export function getTableFeatureFlagsColumnOnFilter(
  value: boolean | Key,
  record: Product,
): boolean {
  if (value === FILTER_WITH_FEATURE_FLAGS) {
    return record.featureFlags?.length > 0;
  }

  if (value === FILTER_WITHOUT_FEATURE_FLAGS) {
    return record.featureFlags?.length === 0;
  }

  return record.featureFlags?.some(
    (ff: IWarehouseProductFeatureFlag): boolean => ff.code === value,
  );
}
